import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { map, Observable, of, shareReplay, tap } from 'rxjs';
import {
  BaseResponse,
  CreatePersonRequest,
  Definition,
  DefinitionBE,
  MiddlewareBaseResponse,
  PersonObjectUpdateResponseModel,
  ProfileApi,
  ProfileDetails,
  ProfileDetailsKey,
  ProfileSubscription,
  SearchRequestType,
  UpdateItem,
} from '@ireckonu/shared-ui';

import { APP_ENV } from '../app-config/app-config.token';
import {
  PersonLookupMappingService,
  PersonLookupSearchInput,
} from './person-lookup-mapping.service';
import { mapSortDirection, toNumberOrNull, trimOrNull } from '../../utils';
import { LOYALTY_LOOKUP_PARAMS } from '../constants';
import {
  LoyaltySearchQueryApiInterface,
  PersonLookupRequestInterface,
  ProfileApiInterface,
  ProfileSearchQueryApiInterface,
  ProfileSearchRequest,
  ProfileSortBy,
  ProfileSortDirection,
  ProfilesSearchQuery,
  ProfilesSearchResponse,
  ServiceUrlKey,
  SortOptionsEnum,
  SortOptionsType,
} from '../models';
import { PersonDataInterface } from '../models/person-lookup-api.interface';
import { ConfigurationQueryService } from './configuration-query.service';

const PROFILES_PAGE_SIZE = 20;

interface ProfileSearchOptions {
  page?: number;
  pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class ProfilesService implements ProfileApi {
  private readonly http = inject(HttpClient);
  private readonly lookupMapping = inject(PersonLookupMappingService);
  private readonly configService = inject(ConfigurationQueryService);
  private readonly configApiBaseUrl = inject(APP_ENV).configApiBaseUrl;
  private readonly staffApiBaseUrl = this.configApiBaseUrl + '/api/v1';

  private cachedDefinitions: Definition<ProfileDetailsKey>[] | null = null;

  private readonly subscriptionsVersion = signal(0);
  private readonly profilesVersion = signal(0);

  /** Signals that consumers can use as rxResource params to trigger reload on invalidation */
  readonly subscriptionsInvalidated = this.subscriptionsVersion.asReadonly();
  readonly profilesInvalidated = this.profilesVersion.asReadonly();

  private readonly profilesCache = new Map<
    string,
    Observable<ProfileApiInterface[]>
  >();
  private readonly profileDetailCache = new Map<
    string,
    Observable<ProfileApiInterface>
  >();
  private readonly subscriptionsCache = new Map<
    string,
    Observable<ProfileSubscription[]>
  >();

  private get lookUpBaseUrl(): string {
    return this.configService.getServiceUrl(ServiceUrlKey.LookUpBaseUrl) ?? '';
  }

  private get subCenterBaseUrl(): string {
    return this.configApiBaseUrl + '/proxy/subscriptioncenter/api/v1';
  }

  searchProfiles(
    query: ProfilesSearchQuery,
    options: ProfileSearchOptions = {},
  ): Observable<ProfilesSearchResponse> {
    const requestBody = this.buildSearchRequest(query, options);

    return this.http
      .post<
        MiddlewareBaseResponse<ProfilesSearchResponse>
      >(`${this.staffApiBaseUrl}/profiles/search-live`, requestBody)
      .pipe(map((response) => response.Data));
  }

  /**
   * Direct person lookup. Still used by loyalty search and venue reservations,
   * which need loyalty-specific lookup params or a lookup by a list of person ids.
   */
  getProfiles(
    query: PersonLookupSearchInput,
    extraLookupParams?: PersonLookupRequestInterface['ExtraLookupParams'],
    areLoyaltyMembersOnly = false,
  ): Observable<ProfileApiInterface[]> {
    if (!PersonLookupMappingService.hasSearchValues(query)) {
      return of<ProfileApiInterface[]>([]);
    }

    const requestBody = extraLookupParams
      ? this.lookupMapping.mapSearchQueryToRequest(query, extraLookupParams)
      : this.lookupMapping.mapSearchQueryToRequest(query);
    const cacheKey = `profiles-${JSON.stringify(requestBody)}`;

    if (this.profilesCache.has(cacheKey)) {
      return this.profilesCache.get(cacheKey);
    }

    const request$ = this.http
      .post<
        MiddlewareBaseResponse<PersonDataInterface[]>
      >(`${this.lookUpBaseUrl}/person/lookup`, requestBody)
      .pipe(
        map((response) => {
          const profiles = this.mapProfilesData(
            response,
            areLoyaltyMembersOnly,
          );

          profiles.forEach((profile) => {
            this.profileDetailCache.set(
              `profile-${profile.id}`,
              of(profile).pipe(shareReplay({ bufferSize: 1, refCount: true })),
            );
          });

          return profiles;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.profilesCache.set(cacheKey, request$);
    return request$;
  }

  private mapProfilesData(
    response: MiddlewareBaseResponse<PersonDataInterface[]>,
    areLoyaltyMembersOnly: boolean,
  ): ProfileApiInterface[] {
    const data = areLoyaltyMembersOnly
      ? response.Data?.filter((person) =>
          person.Memberships?.some((m) => m.Code),
        )
      : response.Data;

    return this.lookupMapping.mapPersonsToProfiles(data ?? []);
  }

  invalidateProfiles(): void {
    this.profilesCache.clear();
    this.profilesVersion.update((v) => v + 1);
  }

  getProfileDetails(
    id: number | string,
    extraLookupParams?: PersonLookupRequestInterface['ExtraLookupParams'],
  ): Observable<ProfileApiInterface> {
    const profileId = String(id);
    const cacheKey = extraLookupParams
      ? `profile-${profileId}-${JSON.stringify(extraLookupParams)}`
      : `profile-${profileId}`;

    if (this.profileDetailCache.has(cacheKey)) {
      return this.profileDetailCache.get(cacheKey);
    }

    const requestBody = extraLookupParams
      ? this.lookupMapping.buildLookupByIdRequest(profileId, extraLookupParams)
      : this.lookupMapping.buildLookupByIdRequest(profileId);

    const request$ = this.http
      .post<
        MiddlewareBaseResponse<PersonDataInterface[]>
      >(`${this.lookUpBaseUrl}/person/lookup`, requestBody)
      .pipe(
        map((response) => {
          const match =
            response.Data?.find(
              (person) => String(person.PersonId) === profileId,
            ) ?? response.Data?.[0];
          if (!match) {
            throw new Error('Profile not found');
          }
          return this.lookupMapping.mapPersonToProfile(match);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.profileDetailCache.set(cacheKey, request$);
    return request$;
  }

  invalidateProfileDetail(id: string): void {
    const cacheKey = `profile-${id}`;
    const loyaltyCacheKey = `profile-${id}-${JSON.stringify(LOYALTY_LOOKUP_PARAMS)}`;

    this.profileDetailCache.delete(cacheKey);
    this.profileDetailCache.delete(loyaltyCacheKey);
  }

  getLoyaltyProfileDetails(
    id: number | string,
  ): Observable<ProfileApiInterface> {
    return this.getProfileDetails(id, LOYALTY_LOOKUP_PARAMS);
  }

  getProfileSubscriptions(
    profileId: string,
  ): Observable<ProfileSubscription[]> {
    const cacheKey = `subscriptions-${profileId}`;

    if (this.subscriptionsCache.has(cacheKey)) {
      return this.subscriptionsCache.get(cacheKey);
    }

    const request$ = this.http
      .get<
        BaseResponse<ProfileSubscription[]>
      >(`${this.subCenterBaseUrl}/PersonSubscriptions/${profileId}`)
      .pipe(
        map((response) => response.data ?? []),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.subscriptionsCache.set(cacheKey, request$);
    return request$;
  }

  private invalidateProfileSubscriptions(profileId: string): void {
    this.subscriptionsCache.delete(`subscriptions-${profileId}`);
    this.subscriptionsVersion.update((v) => v + 1);
  }

  updateProfileSubscriptions(
    profileId: string,
    subscriptions: ProfileSubscription[],
  ): Observable<BaseResponse<void>> {
    return this.http
      .post<
        BaseResponse<void>
      >(`${this.subCenterBaseUrl}/PersonSubscriptions`, { personId: Number(profileId), subscriptions })
      .pipe(
        tap(() => {
          this.invalidateProfileSubscriptions(profileId);
        }),
      );
  }

  updateProfileItem(
    itemsList: UpdateItem[],
    profileId: string | number,
  ): Observable<PersonObjectUpdateResponseModel> {
    const payload = {
      PersonId: profileId,
      Data: itemsList,
    };
    return this.http
      .post<
        MiddlewareBaseResponse<PersonObjectUpdateResponseModel>
      >(`${this.lookUpBaseUrl}/person/updateobjects`, payload)
      .pipe(map((response) => response.Data));
  }

  createProfile(
    payload: CreatePersonRequest,
  ): Observable<MiddlewareBaseResponse<number>> {
    return this.http.post<MiddlewareBaseResponse<number>>(
      `${this.lookUpBaseUrl}/person/create`,
      payload,
    );
  }

  enrollMembership(
    profileId: number,
    programCode: string,
  ): Observable<MiddlewareBaseResponse<unknown>> {
    return this.http.post<MiddlewareBaseResponse<unknown>>(
      `${this.lookUpBaseUrl}/person/enrollmembership`,
      {
        PersonId: profileId,
        ProgramCode: programCode,
      },
    );
  }

  getDefinitions(): Observable<Definition<ProfileDetailsKey>[]> {
    if (this.cachedDefinitions) {
      return of(this.cachedDefinitions);
    }

    return this.http
      .get<
        MiddlewareBaseResponse<DefinitionBE<ProfileDetailsKey>[]>
      >(`${this.lookUpBaseUrl}/person/objecttypedefinitions`)
      .pipe(
        map((data) => {
          if (data?.Data) {
            const definitions: Definition<ProfileDetailsKey>[] = data.Data.map(
              (element) => ({
                ...element,
                PropertyTypes: element.PropertyTypes.map((property) => ({
                  ...property,
                  renderElement: property.UiComponent
                    ? property.UiComponent.Type
                    : 'none',
                  parentProperty: element.Type,
                  UiComponent: property.UiComponent ?? {
                    Type: 'none',
                    DisplayInOverview: false,
                  },
                })),
              }),
            );
            this.cachedDefinitions = definitions;
            return definitions;
          }
          return [];
        }),
      );
  }

  searchProfilesById(
    id: number,
    customTypes?: SearchRequestType[],
  ): Observable<MiddlewareBaseResponse<{ Data: ProfileDetails }[]>> {
    const payload = {
      PersonId: id,
      Types: customTypes ?? [],
    };

    return this.http.post<MiddlewareBaseResponse<{ Data: ProfileDetails }[]>>(
      `${this.lookUpBaseUrl}/person/objects`,
      payload,
    );
  }

  static hasFilterValues(
    obj:
      | ProfilesSearchQuery
      | ProfileSearchQueryApiInterface
      | LoyaltySearchQueryApiInterface,
  ): boolean {
    const excludedFields = ['sort', 'sortDirection'];

    return Object.entries(obj).some(([key, value]) => {
      if (excludedFields.includes(key)) {
        return false;
      }
      if (typeof value === 'string') {
        return trimOrNull(value) !== null;
      }
      return !!value;
    });
  }

  private buildSearchRequest(
    query: ProfilesSearchQuery,
    options: ProfileSearchOptions,
  ): ProfileSearchRequest {
    return {
      PersonId: toNumberOrNull(query.personId),
      FirstName: trimOrNull(query.firstName),
      LastName: trimOrNull(query.lastName),
      Email: trimOrNull(query.email),
      Phone: trimOrNull(query.phone),
      MembershipId: trimOrNull(query.membershipId),
      Location: query.location
        ? { Code: query.location.code, TypeCode: query.location.typeCode }
        : null,
      SortBy: this.mapSortBy(query.sort),
      SortDirection: mapSortDirection(query.sortDirection, {
        ascending: ProfileSortDirection.Ascending,
        descending: ProfileSortDirection.Descending,
      }),
      Page: options.page ?? 0,
      PageSize: options.pageSize ?? PROFILES_PAGE_SIZE,
    };
  }

  private mapSortBy(sort?: SortOptionsType): ProfileSortBy | null {
    switch (sort) {
      case SortOptionsEnum.lastName:
        return ProfileSortBy.LastNameFirstName;
      default:
        return null;
    }
  }
}
